import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, FileDown, ClipboardList, ClipboardCheck, QrCode, CalendarClock, Send, Save, Wheat } from "lucide-react";

const PERIODS = [
  { v: "0", l: "Todo o período" },
  { v: "30", l: "Últimos 30 dias" },
  { v: "90", l: "Últimos 90 dias" },
  { v: "180", l: "Últimos 180 dias" },
  { v: "365", l: "Últimos 12 meses" },
];

export default function Reports() {
  const { can } = useAuth();
  // Agendamento e envio exigem relatorios.edit no backend (_ensure_report_schedule_action).
  const isManager = can("relatorios", "edit");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("todos");
  const [days, setDays] = useState("90");
  const [loading, setLoading] = useState(false);
  const [pgaEnabled, setPgaEnabled] = useState(false);
  const [pgaLoading, setPgaLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const loadSchedule = () => api.get("/reports/schedule").then(({ data }) => setSchedule(data)).catch(() => {});

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/pga/enabled").then(({ data }) => setPgaEnabled(!!data.enabled)).catch(() => {});
    loadSchedule();
  }, []);

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put("/reports/schedule", {
        enabled: schedule.enabled, day_of_month: schedule.day_of_month, period_days: schedule.period_days,
        clients: schedule.clients.map((c) => ({ client_id: c.client_id, enabled: c.enabled })),
      });
      toast.success("Envio agendado salvo");
      loadSchedule();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSchedule(false);
    }
  };

  const sendNow = async (cid) => {
    setSendingId(cid);
    try {
      const { data } = await api.post("/reports/schedule/send-now", { client_id: cid });
      if (data.ok) toast.success(`Relatório enviado para: ${data.sent.join(", ")}`);
      else toast.error(data.error || `Falha no envio${data.failed?.length ? ` para: ${data.failed.join(", ")}` : ""}`);
      loadSchedule();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingId(null);
    }
  };

  const setClientEnabled = (cid, v) =>
    setSchedule((s) => ({ ...s, clients: s.clients.map((c) => (c.client_id === cid ? { ...c, enabled: v } : c)) }));

  const generate = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (clientId !== "todos") q.set("client_id", clientId);
      if (days !== "0") q.set("days", days);
      await downloadFile(`/reports/management/pdf?${q.toString()}`, "relatorio-gerencial.pdf");
      toast.success("Relatório gerencial gerado com sucesso");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao gerar o relatório");
    } finally {
      setLoading(false);
    }
  };

  const generatePga = async () => {
    setPgaLoading(true);
    try {
      const q = new URLSearchParams();
      if (clientId !== "todos") q.set("client_id", clientId);
      if (days !== "0") q.set("days", days);
      await downloadFile(`/reports/pga/pdf?${q.toString()}`, "relatorio-pga.pdf");
      toast.success("Relatório executivo PGA gerado com sucesso");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao gerar o relatório");
    } finally {
      setPgaLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Relatórios PDF</h1>
        <p className="text-sm text-muted-foreground mt-1">Relatórios profissionais com logo, indicadores, fotos, linha do tempo, assinaturas e QR Code de acesso ao sistema.</p>
      </div>

      <Card className="p-6 border-border bg-card" data-testid="management-report-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><FileText size={19} /></div>
          <div>
            <h3 className="font-heading font-semibold text-sm">Relatório Gerencial Consolidado</h3>
            <p className="text-xs text-muted-foreground">KPIs, gráfico mensal, níveis de risco, áreas críticas, pragas por espécie, operacional Astral e SLA por cliente.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-48 h-9" data-testid="report-client-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os clientes</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44 h-9" data-testid="report-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={loading} data-testid="generate-management-report-btn">
            <FileDown size={15} className="mr-2" /> {loading ? "Gerando PDF..." : "Gerar Relatório (PDF)"}
          </Button>
        </div>
      </Card>

      {pgaEnabled && (
        <Card className="p-6 border-border bg-card" data-testid="pga-report-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-yellow-500/15 text-yellow-500 flex items-center justify-center"><Wheat size={19} /></div>
            <div>
              <h3 className="font-heading font-semibold text-sm">Relatório Executivo — Pragas em Grãos Armazenados (PGA)</h3>
              <p className="text-xs text-muted-foreground">Índice de infestação por estrutura, distribuição por nível, capturas por espécie, evolução mensal e recomendações técnicas da IA.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted-foreground">Usa os filtros de cliente e período selecionados acima.</p>
            <Button variant="outline" onClick={generatePga} disabled={pgaLoading} className="ml-auto" data-testid="generate-pga-report-btn">
              <FileDown size={15} className="mr-2" /> {pgaLoading ? "Gerando PDF..." : "Gerar Relatório PGA (PDF)"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-border bg-card" data-testid="occurrence-report-info-card">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-500/15 text-blue-400 flex items-center justify-center"><ClipboardList size={17} /></div>
            <h3 className="font-heading font-semibold text-sm">PDF de Ocorrência</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Relatório individual com protocolo, semáforo de SLA, evidências fotográficas, validação IA, linha do tempo e assinaturas. Disponível pelo botão <span className="font-semibold">PDF</span> no detalhe de cada ocorrência.</p>
        </Card>
        <Card className="p-5 border-border bg-card" data-testid="plan-report-info-card">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><ClipboardCheck size={17} /></div>
            <h3 className="font-heading font-semibold text-sm">PDF de Plano de Ação</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Relatório com causa raiz, checklist de ações, narrativa técnica da IA, evidências e efetividade. Disponível pelo botão <span className="font-semibold">PDF</span> no detalhe de cada plano de ação.</p>
        </Card>
      </div>

      {schedule && (
        <Card className="p-6 border-border bg-card" data-testid="schedule-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-500/15 text-violet-400 flex items-center justify-center"><CalendarClock size={19} /></div>
              <div>
                <h3 className="font-heading font-semibold text-sm">Envio Agendado por E-mail</h3>
                <p className="text-xs text-muted-foreground">Relatório gerencial enviado automaticamente todo mês aos usuários de perfil Cliente e Gestor de cada cliente.</p>
              </div>
            </div>
            {isManager && (
              <label className="flex items-center gap-2 text-xs">
                Ativo
                <Switch checked={!!schedule.enabled} onCheckedChange={(v) => setSchedule((s) => ({ ...s, enabled: v }))} data-testid="schedule-enabled-switch" />
              </label>
            )}
          </div>

          {isManager && (
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Dia do mês</p>
                <Input type="number" min={1} max={28} value={schedule.day_of_month} className="h-9 w-24 text-center"
                  onChange={(e) => setSchedule((s) => ({ ...s, day_of_month: Math.min(28, Math.max(1, parseInt(e.target.value || "1"))) }))}
                  data-testid="schedule-day-input" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Período do relatório</p>
                <Select value={String(schedule.period_days)} onValueChange={(v) => setSchedule((s) => ({ ...s, period_days: parseInt(v) }))}>
                  <SelectTrigger className="w-40 h-9" data-testid="schedule-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">Últimos 30 dias</SelectItem>
                    <SelectItem value="60">Últimos 60 dias</SelectItem>
                    <SelectItem value="90">Últimos 90 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={saveSchedule} disabled={savingSchedule} data-testid="schedule-save-btn">
                <Save size={14} className="mr-2" /> {savingSchedule ? "Salvando..." : "Salvar agendamento"}
              </Button>
            </div>
          )}

          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="p-2.5 font-medium">Cliente</th>
                  <th className="p-2.5 font-medium">Destinatários</th>
                  <th className="p-2.5 font-medium">Último envio</th>
                  {isManager && <th className="p-2.5 font-medium">Envio mensal</th>}
                  {isManager && <th className="p-2.5 font-medium text-right">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {schedule.clients.map((c) => (
                  <tr key={c.client_id} className="border-b border-border/50 last:border-0" data-testid={`schedule-row-${c.client_id}`}>
                    <td className="p-2.5 font-medium">{c.name}</td>
                    <td className="p-2.5 text-xs text-muted-foreground">
                      {c.recipients.length ? c.recipients.join(", ") : <span className="text-amber-400">Nenhum usuário Cliente/Gestor vinculado</span>}
                    </td>
                    <td className="p-2.5 text-xs">
                      {c.last_sent_at ? (
                        <span className="flex items-center gap-2 flex-wrap">
                          {formatDate(c.last_sent_at)}
                          <Badge variant="outline" className={
                            c.last_status === "enviado" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                              : c.last_status === "sem_destinatarios" ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                                : "bg-red-500/15 text-red-400 border-red-500/30"
                          }>
                            {c.last_status === "enviado" ? "Enviado" : c.last_status === "sem_destinatarios" ? "Sem destinatários" : "Falha"}
                          </Badge>
                        </span>
                      ) : <span className="text-muted-foreground">Nunca enviado</span>}
                    </td>
                    {isManager && (
                      <td className="p-2.5">
                        <Switch checked={!!c.enabled} onCheckedChange={(v) => setClientEnabled(c.client_id, v)} data-testid={`schedule-toggle-${c.client_id}`} />
                      </td>
                    )}
                    {isManager && (
                      <td className="p-2.5 text-right">
                        <Button variant="outline" size="sm" onClick={() => sendNow(c.client_id)}
                          disabled={sendingId === c.client_id || !c.recipients.length} data-testid={`schedule-send-now-${c.client_id}`}>
                          <Send size={13} className="mr-1.5" /> {sendingId === c.client_id ? "Enviando..." : "Enviar agora"}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-4 border-border bg-card flex items-center gap-3" data-testid="qr-info-card">
        <QrCode size={18} className="text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground">Todos os PDFs incluem um QR Code que leva diretamente ao registro correspondente no sistema, permitindo validação e rastreabilidade em auditorias.</p>
      </Card>
    </div>
  );
}
